import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@uniquegood/realworld-studio-design';
import { css } from '@emotion/react';
import { usePlanLimits } from './usePlanLimits';
import useRequest from './useRequest';
import useModalState from './useModalState';
import usePromotionModal from './usePromotionModal';
import { PromotionTriggerLocation } from '@/track';

/**
 * 요금제 다운그레이드로 인한 콘텐츠 제한 초과 시 저장을 막는 Hook
 *
 * @example
 * ```tsx
 * const { checkSaveLimitAndExecute, modal } = useSaveLimitCheck();
 *
 * const onSubmit = () => {
 *   checkSaveLimitAndExecute(() => {
 *     // 실제 저장 로직
 *     saveData();
 *   });
 * };
 *
 * return (
 *   <>
 *     <BottomFloatingBar onSave={onSubmit} />
 *     <Modal {...modal} />
 *   </>
 * );
 * ```
 */
export default function useSaveLimitCheck() {
  const { appId } = useParams<AppParam>();
  const { plan, limits, getPlanName } = usePlanLimits();
  const { modal, openModal, closeModal } = useModalState();
  const { openPromotionModal } = usePromotionModal();

  // 현재 프로젝트 목록 조회
  const { data: projects } = useRequest<CommonResponseModel<Project[]>>(
    `/apps/${appId}/projects`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  const projectCount = projects?.data?.length ?? 0;
  const { maxProjects } = useMemo(() => limits, [limits]);

  // 제한 초과 여부 (무제한이 아니고, 현재 개수가 제한을 초과한 경우)
  const isOverLimit = maxProjects !== null && projectCount > maxProjects;

  /**
   * 저장 전 제한 체크 후 실행
   * @param onSuccess 제한을 초과하지 않았을 때 실행할 콜백
   */
  const checkSaveLimitAndExecute = useCallback(
    (onSuccess: () => void) => {
      if (!isOverLimit) {
        onSuccess();
        return;
      }

      if (plan === 'Free' || plan === 'Pro' || plan === 'Teacher') {
        // 제한 초과 모달 표시
        openModal({
          title: '수정사항을 저장할 수 없어요',
          children: (
            <>
              <Typography as="p" type="body">
                요금제가 {getPlanName(plan)}로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다.
              </Typography>
              <Typography as="p" type="body" cssStyle={marginTopStyle}>
                콘텐츠를 {maxProjects}개 이하로 줄이거나 요금제를 업그레이드해 주세요.
              </Typography>
            </>
          ),
          primaryAction: {
            content: '확인',
            onAction: () => {
              closeModal();
            }
          },
          secondaryAction: {
            content: '요금제 업그레이드',
            onAction: () => {
              closeModal();
              openPromotionModal({
                triggerLocation: PromotionTriggerLocation.content_save_blocked_content_limit
              });
            }
          }
        });
      }
    },
    [isOverLimit, maxProjects, projectCount, openModal, closeModal]
  );

  return {
    /** 저장 전 제한 체크 후 실행 */
    checkSaveLimitAndExecute,
    /** 모달 props */
    modal,
    /** 모달 닫기 */
    closeModal,
    /** 제한 초과 여부 */
    isOverLimit,
    /** 현재 프로젝트 개수 */
    projectCount,
    /** 최대 프로젝트 개수 (null이면 무제한) */
    maxProjects
  };
}

const marginTopStyle = css`
  margin-top: 8px;
`;
